/**
 * Grouped-reminder model (REGTA-51).
 *
 * A template rule with multiple lead times materializes one notification
 * row per lead (e.g. lease_end at 3mo/1mo/1wk) — three rows for ONE
 * lifecycle event. This module folds such rows back into one group so
 * the UI can present a single reminder that exposes its schedule.
 *
 * Group identity = contract + phase + kind + meta.ruleKey + triggerAt:
 * the same rule anchored to the same lifecycle date. Rows without a
 * ruleKey (user-created custom reminders) are singleton groups — there
 * is no configuration tying them together.
 */

import type {
  NotificationKind,
  NotificationLead,
  NotificationPhase,
} from '../api.js';

export type ReminderGroupRow = {
  id: string;
  contractId?: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  leadLabel: NotificationLead;
  triggerAt: string;
  fireAt: string;
  acknowledgedAt?: string | null;
  meta: Record<string, unknown>;
};

export type ReminderGroup<R extends ReminderGroupRow> = {
  key: string;
  kind: NotificationKind;
  phase: NotificationPhase;
  triggerAt: string;
  /** meta.label when every row carries the same one (custom rows,
   * single-occurrence rules) — null when occurrences have their own
   * labels (in-flight escalation steps) or none at all. */
  sharedLabel: string | null;
  /** All rows, fireAt ascending — the group's full schedule. */
  rows: R[];
  /** Unacknowledged rows. */
  active: R[];
  /** Unacknowledged rows whose fireAt has passed — the triggered set. */
  due: R[];
  /** Freshest triggered occurrence (surfaced while the group is due). */
  current: R | null;
  /** Earliest future unacknowledged occurrence. */
  next: R | null;
  isDue: boolean;
  allHandled: boolean;
};

export function reminderRowLabel(row: ReminderGroupRow): string | null {
  const label = (row.meta as { label?: unknown }).label;
  return typeof label === 'string' && label ? label : null;
}

export function reminderGroupKey(row: ReminderGroupRow): string {
  const ruleKey = (row.meta as { ruleKey?: unknown }).ruleKey;
  if (typeof ruleKey !== 'string' || !ruleKey) return `row:${row.id}`;
  return [row.contractId ?? '', row.phase, row.kind, ruleKey, row.triggerAt].join('|');
}

export function groupReminders<R extends ReminderGroupRow>(
  rows: R[],
  now = new Date(),
): Array<ReminderGroup<R>> {
  const byKey = new Map<string, R[]>();
  for (const row of rows) {
    const key = reminderGroupKey(row);
    const list = byKey.get(key);
    if (list) list.push(row);
    else byKey.set(key, [row]);
  }

  const nowMs = now.getTime();
  const groups: Array<ReminderGroup<R>> = [];
  for (const [key, groupRows] of byKey) {
    groupRows.sort(
      (a, b) => a.fireAt.localeCompare(b.fireAt) || a.id.localeCompare(b.id),
    );
    const active = groupRows.filter((r) => !r.acknowledgedAt);
    // `active` is fireAt-ascending, so the first future row splits it
    // into the triggered prefix (`due`) and the upcoming rest.
    const firstFuture = active.findIndex(
      (r) => new Date(r.fireAt).getTime() > nowMs,
    );
    const due = firstFuture === -1 ? active : active.slice(0, firstFuture);
    const labels = new Set(groupRows.map((r) => reminderRowLabel(r) ?? ''));
    const first = groupRows[0]!;
    groups.push({
      key,
      kind: first.kind,
      phase: first.phase,
      triggerAt: first.triggerAt,
      sharedLabel: labels.size === 1 ? reminderRowLabel(first) : null,
      rows: groupRows,
      active,
      due,
      current: due.length > 0 ? due[due.length - 1]! : null,
      next: firstFuture === -1 ? null : active[firstFuture]!,
      isDue: due.length > 0,
      allHandled: active.length === 0,
    });
  }

  groups.sort(compareGroups);
  return groups;
}

/**
 * Due groups first (freshest triggered on top, mirroring list_due),
 * then upcoming groups by soonest next occurrence, fully-handled
 * groups last (most recently handled first).
 */
function compareGroups(
  a: ReminderGroup<ReminderGroupRow>,
  b: ReminderGroup<ReminderGroupRow>,
): number {
  const rank = (g: ReminderGroup<ReminderGroupRow>) =>
    g.isDue ? 0 : g.allHandled ? 2 : 1;
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  if (a.isDue) return b.current!.fireAt.localeCompare(a.current!.fireAt);
  if (!a.allHandled) {
    return (a.next?.fireAt ?? '').localeCompare(b.next?.fireAt ?? '');
  }
  const lastAck = (g: ReminderGroup<ReminderGroupRow>) =>
    g.rows.reduce(
      (m, r) => (r.acknowledgedAt && r.acknowledgedAt > m ? r.acknowledgedAt : m),
      '',
    );
  return lastAck(b).localeCompare(lastAck(a));
}
